import _ from 'lodash';
import {useEffect, useState} from 'react';

import {safeLocalStorage} from './localStorage';
import wait from './wait';

const BEAMER_INIT_RETRIES = 10;

let newPosts = 0;
const newPostsListeners: {[key: string]: (newPosts: number) => void} = {};

function updateNewPosts(np: number): void {
  newPosts = np;
  for (const fn of Object.values(newPostsListeners)) {
    fn(np);
  }
}

export function useBeamerNewPosts(): number {
  const [np, setNP] = useState(newPosts);

  useEffect(() => {
    const key = Math.random();
    newPostsListeners[key] = setNP;
    return () => {
      delete newPostsListeners[key];
    };
  }, []);

  return np;
}

let beamerAvailability: boolean | null = null;
const availabilityListeners: {
  [key: string]: (available: boolean | null) => void;
} = {};

function updateBeamerAvailability(available: boolean): void {
  beamerAvailability = available;
  for (const fn of Object.values(availabilityListeners)) {
    fn(available);
  }
}

export function useBeamerAvailable(): boolean | null {
  const [available, setAvailable] = useState(beamerAvailability);

  useEffect(() => {
    const key = Math.random();
    availabilityListeners[key] = setAvailable;
    return () => {
      delete availabilityListeners[key];
    };
  }, []);

  return available;
}

export function useBeamerHasNewPosts(): boolean {
  return useBeamerNewPosts() > 0;
}

export const beamerInit = _.once(() => {
  // eslint-disable-next-line no-async-promise-executor
  return new Promise(async resolveInit => {
    let tries = 0;
    while (window.Beamer == null && tries < BEAMER_INIT_RETRIES) {
      tries++;
      await wait(500);
    }
    const b = window.Beamer;
    if (b == null) {
      updateBeamerAvailability(false);
      resolveInit(false);
      return;
    }
    b.update({
      callback: (np: number) => {
        updateBeamerAvailability(true);
        updateNewPosts(np);
        resolveInit(true);
      },
      onopen: () => {
        updateNewPosts(0);
      },
    });
    b.init();
  });
});

export async function openBeamer() {
  if (await beamerInit()) {
    window.Beamer.show();
  }
}

export function disableBeamer() {
  safeLocalStorage.setItem(
    'disableBeamerUntil',
    // Disabled for 1 day
    (Date.now() + 1000 * 60 * 60 * 24 * 1).toString()
  );
}

export function openBeamerOnLoad() {
  if (
    safeLocalStorage.getItem('disableBeamerUntil') == null ||
    Date.now() >= Number(safeLocalStorage.getItem('disableBeamerUntil'))
  ) {
    beamerInit();
  }
}
